import { useRef, useState } from 'react';
import { Drawer, App, Radio, Upload, Button, Form, Input, Flex, Space, Progress } from 'antd';
import type { UploadProps } from 'antd';
import { usePortalCtx } from '../context.js';
import { apiPost, apiPut } from '../api.js';
import { fromJsonl } from '../util/jsonl.js';

type Format = 'json' | 'jsonl';

type ImportRow = { key: string; value: string | unknown; siteKey?: string };

function parsePayload(input: string, fmt: Format): ImportRow[] {
  const raw: unknown[] = fmt === 'json' ? JSON.parse(input) : fromJsonl(input);
  if (!Array.isArray(raw)) throw new Error('Expected an array of entries');
  return raw.map((r) => {
    const o = r as Record<string, unknown>;
    const key = String(o.key ?? '');
    if (!key) throw new Error('Missing key in some row');
    const siteKey = typeof o.siteKey === 'string' ? o.siteKey : undefined;
    const v = o.value as unknown;
    const value = typeof v === 'string' ? v : JSON.stringify(v);
    return { key, value, siteKey };
  });
}

export default function ConfigBulkImportDrawer(props: {
  open: boolean;
  onClose: () => void;
  onUpsertLocal: (rows: { key: string; value: string; siteKey?: string }[]) => void;
}) {
  const { open, onClose, onUpsertLocal } = props;
  const { registryUrl } = usePortalCtx();
  const { message } = App.useApp();

  const [fmt, setFmt] = useState<Format>('json');
  const [text, setText] = useState<string>('');
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(0);
  const totalRef = useRef(0);

  const uploadProps: UploadProps = {
    accept: '.json,.jsonl',
    beforeUpload: async (file) => {
      const content = await file.text();
      setText(content);
      setFmt(file.name.endsWith('.jsonl') ? 'jsonl' : 'json');
      return false;
    },
  };

  async function runImport() {
    try {
      const rows = parsePayload(text, fmt);
      if (!rows.length) {
        message.info('No entries to import');
        return;
      }

      setRunning(true);
      setDone(0);
      totalRef.current = rows.length;

      const updated: { key: string; value: string; siteKey?: string }[] = [];
      for (const r of rows) {
        const url = new URL('/admin/config', registryUrl);
        try {
          const res = await apiPost<{ key: string; value: string; siteKey?: string }>(
            url.toString(),
            r,
          );
          updated.push(res);
        } catch {
          const res = await apiPut<{ key: string; value: string; siteKey?: string }>(
            url.toString(),
            r,
          );
          updated.push(res);
        } finally {
          setDone((x) => x + 1);
        }
      }

      onUpsertLocal(updated);
      message.success(`Imported ${updated.length}/${rows.length} entries`);
      onClose();
    } catch (e) {
      message.error(e instanceof Error ? e.message : 'Import failed');
    } finally {
      setRunning(false);
    }
  }

  const percent = totalRef.current ? Math.round((done / totalRef.current) * 100) : 0;

  return (
    <Drawer title="Import Config" open={open} onClose={onClose} size={720} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Flex align="center" justify="space-between">
          <Radio.Group value={fmt} onChange={(e) => setFmt(e.target.value)}>
            <Radio.Button value="json">JSON array</Radio.Button>
            <Radio.Button value="jsonl">JSONL</Radio.Button>
          </Radio.Group>
          <Upload {...uploadProps}>
            <Button>Choose file…</Button>
          </Upload>
        </Flex>

        <Form layout="vertical">
          <Form.Item label={fmt === 'json' ? 'JSON array' : 'JSONL (one JSON object per line)'}>
            <Input.TextArea
              autoSize={{ minRows: 8, maxRows: 16 }}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={
                fmt === 'json'
                  ? `[{"key":"consent.banner.enabled","value":true,"siteKey":"howth-yc"}]`
                  : `{"key":"consent.banner.enabled","value":true,"siteKey":"howth-yc"}`
              }
            />
          </Form.Item>
        </Form>

        {running && <Progress percent={percent} />}

        <Flex justify="end" gap={8}>
          <Button onClick={onClose} disabled={running}>
            Cancel
          </Button>
          <Button type="primary" onClick={runImport} loading={running}>
            Import
          </Button>
        </Flex>
      </Space>
    </Drawer>
  );
}
